import type { HostServices } from "@amzn/ytyard360-agent-studio";
import { store } from "@/store/index";
import { ENDPOINTS, RYC_WEBSOCKET } from "@/app/app-config";
import { getSecurityToken } from "@/federation/getSecurityToken";
import type { PermissionsState } from "@/features/yard360/store/permissions/types";
import { pendoService } from "@/services/pendo/Pendo";
import rumService, { RUM_EVENT_TYPES, type RumEventType } from "@/utils/awsRUM/awsRUM";

interface AuthRealmDomain {
  realm: string;
  domain: string;
  context?: { userId?: string; userName?: string };
}

type AgentStudioHostServices = HostServices & {
  getRycWebsocketEndpoint: () => string;
  getUserId: () => string | null;
  getUserAlias: () => string | null;
  getGrantedPermissions: () => string[];
  telemetry?: {
    trackPendoEvent?: (eventName: string, data?: Record<string, unknown>) => void;
    trackRumCustomEvent?: (event: {
      type: string;
      message: string;
      data?: Record<string, unknown>;
    }) => void;
    trackRumError?: (message: string, error: unknown, data?: Record<string, unknown>) => void;
  };
};

const getServiceUrl = (service: string): string => {
  const { authInfo } = store.getState() as { authInfo?: Partial<AuthRealmDomain> };
  const realm = authInfo?.realm;
  const domain = authInfo?.domain;
  if (!realm || !domain) {
    throw new Error("agentStudioHostServices: authInfo realm/domain not initialized");
  }
  return ENDPOINTS[realm][domain][service];
};

/**
 * The host's conformance to the Agent Studio package's HostServices interface
 * (consumer-defines-the-interface DI). Passed to <AgentStudioApp hostServices={...}>
 * at the lazy route. Module singleton so the prop is referentially stable.
 */
export const agentStudioHostServices: AgentStudioHostServices = {
  getSecurityToken,
  getYardConsoleEndpoint: () => getServiceUrl("YardConsoleService"),
  getYard360Endpoint: () => getServiceUrl("Yard360Service"),
  getRycWebsocketEndpoint: () => getServiceUrl(RYC_WEBSOCKET),
  getUserId: () => {
    const { authInfo } = store.getState() as { authInfo?: AuthRealmDomain };
    return authInfo?.context?.userId ?? null;
  },
  getUserAlias: () => {
    // The Amazon alias is the userName minus the @domain, matching the
    // host's alias convention (see useAppInitialization).
    const { authInfo } = store.getState() as { authInfo?: AuthRealmDomain };
    return authInfo?.context?.userName?.split("@")[0] ?? null;
  },
  getGrantedPermissions: () => {
    const { permissions } = store.getState() as { permissions?: PermissionsState };
    return permissions?.agentStudio?.grantedPermissions ?? [];
  },
  telemetry: {
    trackPendoEvent: (eventName: string, data?: Record<string, unknown>) =>
      pendoService.trackEvent(eventName, data),
    trackRumCustomEvent: (event: {
      type: string;
      message: string;
      data?: Record<string, unknown>;
    }) =>
      rumService.customEvent({
        type: (Object.values(RUM_EVENT_TYPES) as string[]).includes(event.type)
          ? (event.type as RumEventType)
          : RUM_EVENT_TYPES.USER_ACTION,
        message: event.message,
        data: event.data,
      }),
    trackRumError: (message: string, error: unknown, data?: Record<string, unknown>) =>
      // recordError needs a real Error to preserve the stack for grouping.
      rumService.error(message, error instanceof Error ? error : new Error(String(error)), data),
  },
};
